/**
 * useBlockModifiers - Hook for analyzing block children and extracting modifiers
 *
 * Modifiers are special child blocks that modify how their parent block renders,
 * rather than rendering themselves. This hook scans a block's children and returns
 * an array of modifier configurations.
 */

import type { PlayBlock_Union } from '@/types/blocks'
import type { ControlPointBlock } from '@/types/blocks/canvas/ControlPoint'
import type { BlockModifier } from './Types'
import { createPreSnapModifier } from './PreSnapModifier'

/**
 * Extract __resolved_children from a block's injected data
 * Data loaders inject resolved children as a non-schema property
 */
function getResolvedChildren(block: PlayBlock_Union): PlayBlock_Union[] | undefined {
	// Use unknown intermediate to safely access injected non-schema property
	const resolved = (block as unknown as { __resolved_children?: PlayBlock_Union[] }).__resolved_children
	return resolved
}

function findChildByType(
	block: PlayBlock_Union,
	blockType: string
): PlayBlock_Union | undefined {
	const resolvedChildren = getResolvedChildren(block)
	if (!resolvedChildren) return undefined

	return resolvedChildren.find(
		(child: PlayBlock_Union) => child.type === blockType
	)
}

/**
 * Extract resolved control points from a block's injected properties
 */
function getResolvedControlPoints(block: PlayBlock_Union): ControlPointBlock[] | undefined {
	const resolvedChildren = getResolvedChildren(block)
	if (!resolvedChildren) return undefined

	return resolvedChildren.filter(
		(child: PlayBlock_Union) => child.type === 'control_point'
	) as ControlPointBlock[]
}

/**
 * Analyzes a block's children and returns modifiers that affect rendering
 *
 * @param block - The parent block to analyze
 * @returns Array of modifiers extracted from child blocks
 *
 * @example
 * ```typescript
 * const drawingBlock = { ... }
 * const modifiers = useBlockModifiers(drawingBlock)
 *
 * const pathSplitMod = modifiers.find(m => m.type === 'path_split')
 * if (pathSplitMod) {
 *   return <SplitPathRenderer modifier={pathSplitMod} />
 * }
 * ```
 */
export function useBlockModifiers(block: PlayBlock_Union): BlockModifier[] {
	const modifiers: BlockModifier[] = []

	// Check for pre-snap marker children
	const preSnapMarker = findChildByType(block, 'pre_snap_marker')
	if (preSnapMarker) {
		const controlPoints = getResolvedControlPoints(block)
		modifiers.push(createPreSnapModifier(preSnapMarker, controlPoints))
	}

	return modifiers
}
